import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { NgClass, NgFor, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { forkJoin } from "rxjs";
import swal from "sweetalert2";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Empleado } from "src/app/shared/model/empleado/empleado.model";
import { Persona } from "src/app/shared/model/security/persona.model";
import { Usuario } from "src/app/shared/model/security/usuario.model";
import { EmpleadoService } from "../empleado.service";

@Component({
  selector: "app-gestion-empleados",
  standalone: true,
  templateUrl: "./gestion-empleados.component.html",
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    NgFor,
    NgIf,
    NgClass,
    MatButtonModule,
    MatSlideToggleModule,
  ],
})
export class GestionEmpleadosComponent implements OnInit {
  listEmpleados: Array<Empleado> = [];
  consultaObject = new ConsultaList<Empleado>();
  totalRegistros = 0;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  currentPage = 1;
  loading = false;
  empleadoSelec = new Empleado();
  searchTerm = "";

  constructor(
    private router: Router,
    private empleadoService: EmpleadoService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.consultaObject.bean = new Empleado();
    this.consultaObject.bean.persona = new Persona();
    this.consultaObject.bean.usuario = new Usuario();
    this.restoreListState();
    this.consultaPagina(this.currentPage);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.loading = this.listEmpleados.length === 0;
    this.cdr.detectChanges();
    this.consultaObject.regInicial = (page - 1) * this.pageSize;
    this.consultaObject.regXPagina = this.pageSize;

    forkJoin({
      total: this.empleadoService.consultaCantidadEmpleados(this.consultaObject),
      empleados: this.empleadoService.consultaEmpleados(this.consultaObject),
    }).subscribe({
      next: ({ total, empleados }) => {
        this.totalRegistros = this.resolveTotalRegistros(total, empleados);
        this.listEmpleados = Array.isArray(empleados?.list) ? empleados.list : [];

        const maxPages = this.totalPages;
        if (maxPages > 0 && this.currentPage > maxPages) {
          this.consultaPagina(maxPages);
          return;
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listEmpleados = [];
        this.totalRegistros = 0;
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  onBusquedaEmpleadoChange(value: string) {
    this.searchTerm = value ?? "";
    this.consultaObject.bean.persona.nombre = this.searchTerm;
    this.consultaPagina(1);
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  onClicMostrarModalEmpleado(empleadoEdit: Empleado | null): void {
    const listState = this.buildListState();

    if (empleadoEdit == null) {
      this.router.navigate(["/EMPLEADO/GESTION_EMPLEADOS/alta"], {
        state: { listState },
      });
      return;
    }

    const empleadoId =
      empleadoEdit?.usuario?.id ??
      empleadoEdit?.idUsuario ??
      empleadoEdit?.idEmpleado ??
      0;

    this.router.navigate(["/EMPLEADO/GESTION_EMPLEADOS/edicion", empleadoId], {
      state: {
        empleado: this.cloneEmpleado(empleadoEdit),
        listState,
      },
    });
  }

  onClicAcualizaStatus(usuarioEdit: Usuario, check: any): void {
    if (usuarioEdit != undefined) {
      const esBaja = usuarioEdit.estatus === "ACTIVO";
      const nomOperacion = esBaja ? "Baja empleado" : "Activar empleado";
      // La baja revoca los teléfonos en el servidor (UsuarioBuss.updateStatus)
      // y reactivar no los devuelve: se avisa antes, igual que en el kiosco.
      // Un BLOQUEADO (contraseña equivocada varias veces) nunca perdió sus
      // teléfonos: desbloquearlo no pide volver a vincular nada.
      const consecuencia = esBaja
        ? "Dejará de poder entrar al sistema y de registrar asistencia, y sus teléfonos vinculados se desactivan."
        : usuarioEdit.estatus === "BLOQUEADO"
          ? "Se desbloqueará y volverá a poder entrar al sistema. Sus teléfonos siguen vinculados."
          : "Volverá a poder entrar al sistema y a registrar asistencia. Si registraba desde su teléfono, tendrá que vincularlo otra vez.";
      swal
        .fire({
          title: nomOperacion,
          text: `${consecuencia} ¿Desea continuar?`,
          buttonsStyling: false,
          icon: "warning",
          confirmButtonText: "Aceptar",
          customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-info",
          },
          cancelButtonText: "Cancelar",
          showConfirmButton: true,
          showCancelButton: true,
        })
        .then((actualizar) => {
          if (actualizar.value) {
            this.empleadoService
              .actualizaStatusEmpleado(usuarioEdit)
              .subscribe(() => {
                this.consultaPagina(this.currentPage);
              });
          } else {
            check.source.checked = !check.source.checked;
            this.cdr.detectChanges();
          }
        });
    }
  }

  get totalPages(): number {
    return this.totalRegistros > 0
      ? Math.ceil(this.totalRegistros / this.pageSize)
      : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getStatusLabel(empleado: Empleado): string {
    return empleado?.usuario?.estatus === "ACTIVO" ? "Activo" : "Inactivo";
  }

  getStatusClass(empleado: Empleado): string {
    return empleado?.usuario?.estatus === "ACTIVO" ? "is-active" : "is-inactive";
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  private restoreListState(): void {
    const listState = history.state?.listState as
      | {
          currentPage?: number;
          pageSize?: number;
          searchTerm?: string;
        }
      | undefined;

    if (!listState) {
      return;
    }

    const restoredPageSize = Number(listState.pageSize);
    const restoredPage = Number(listState.currentPage);

    if (Number.isFinite(restoredPageSize) && restoredPageSize > 0) {
      this.pageSize = restoredPageSize;
    }

    if (Number.isFinite(restoredPage) && restoredPage > 0) {
      this.currentPage = restoredPage;
    }

    if (typeof listState.searchTerm === "string") {
      this.searchTerm = listState.searchTerm;
      this.consultaObject.bean.persona.nombre = this.searchTerm;
    }
  }

  private buildListState(): {
    currentPage: number;
    pageSize: number;
    searchTerm: string;
  } {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
    };
  }

  private cloneEmpleado(empleado: Empleado): Empleado {
    return JSON.parse(JSON.stringify(empleado)) as Empleado;
  }

  private resolveTotalRegistros(total: unknown, empleados: ConsultaList<Empleado>): number {
    const totalResponse = total as Record<string, unknown> | null;
    const empleadosResponse = empleados as ConsultaList<Empleado> & Record<string, unknown>;
    const candidates = [
      totalResponse?.pTotalItems,
      totalResponse?.totalItems,
      totalResponse?.totalRegistros,
      empleadosResponse?.pTotalItems,
      empleadosResponse?.totalItems,
      empleadosResponse?.totalRegistros,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(empleados?.list) ? empleados.list.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
